import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import api from '../../../../services/apiConfig';
import styles from './PaymentConditionSelect.module.css';

interface PaymentCondition {
    ID: number;
    DESCRICAO: string;
    PRAZO_MEDIO: number;
    PDD?: number;
    PRAZO_PADRAO?: number;
    TAXA?: number;
}

interface PaymentConditionSelectProps {
    onSelect: (condition: PaymentCondition) => void;
    placeholder?: string;
    initialValue?: string;
}

const PaymentConditionSelect: React.FC<PaymentConditionSelectProps> = ({ onSelect, placeholder = "Selecione a condição...", initialValue = '' }) => {
    const [conditions, setConditions] = useState<PaymentCondition[]>([]);
    const [filteredConditions, setFilteredConditions] = useState<PaymentCondition[]>([]);
    const [searchTerm, setSearchTerm] = useState(initialValue);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
    const [selectedIndex, setSelectedIndex] = useState(-1);
    
    const wrapperRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchConditions = async () => {
            setLoading(true);
            try {
                const response = await api.get('/orders/payment-conditions');
                if (Array.isArray(response.data)) {
                    setConditions(response.data);
                    setFilteredConditions(response.data);
                }
            } catch (error) {
                console.error("Erro ao buscar condições de pagamento:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchConditions();
    }, []);

    useEffect(() => {
        if (!searchTerm) {
            setFilteredConditions(conditions);
        } else {
            const term = searchTerm.toLowerCase();
            const filtered = conditions.filter(c => 
                c.DESCRICAO.toLowerCase().includes(term) || 
                String(c.ID).includes(term)
            );
            setFilteredConditions(filtered);
        }
        setSelectedIndex(-1);
    }, [searchTerm, conditions]);

    React.useLayoutEffect(() => {
        const updatePosition = () => {
            if (isOpen && wrapperRef.current) {
                const rect = wrapperRef.current.getBoundingClientRect();
                
                const spaceBelow = window.innerHeight - rect.bottom;
                const dropdownHeight = 250; 
                
                setDropdownStyle({
                    position: 'fixed',
                    top: spaceBelow < dropdownHeight && rect.top > dropdownHeight ? 'auto' : rect.bottom + 8,
                    bottom: spaceBelow < dropdownHeight && rect.top > dropdownHeight ? window.innerHeight - rect.top + 8 : 'auto',
                    left: rect.left,
                    right: 'auto',
                    width: rect.width,
                    zIndex: 99999
                });
            }
        };

        if (isOpen) {
            updatePosition();
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
        }

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                const target = event.target as Element;
                if (!target.closest(`.${styles.dropdown}`)) {
                    setIsOpen(false);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (condition: PaymentCondition) => {
        onSelect(condition);
        setSearchTerm(condition.DESCRICAO);
        setIsOpen(false);
    };

    const handleFocus = () => {
        setIsOpen(true);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                if (filteredConditions.length > 0) setIsOpen(true);
            }
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex((prev) => {
                const nextIndex = prev < filteredConditions.length - 1 ? prev + 1 : prev;
                scrollToIndex(nextIndex);
                return nextIndex;
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex((prev) => {
                const nextIndex = prev > 0 ? prev - 1 : prev;
                scrollToIndex(nextIndex);
                return nextIndex;
            });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedIndex >= 0 && selectedIndex < filteredConditions.length) {
                handleSelect(filteredConditions[selectedIndex]);
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    const scrollToIndex = (index: number) => {
        if (!listRef.current) return;
        // Pula o primeiro child que é o header
        const item = listRef.current.children[index + 1] as HTMLElement;
        if (item) {
            item.scrollIntoView({ block: 'nearest' });
        }
    };

    return (
        <div className={styles.container} ref={wrapperRef}>
            <input
                type="text"
                className={styles.input}
                placeholder={placeholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
            />
            
            {isOpen && ReactDOM.createPortal(
                <div className={styles.dropdown} style={{ ...dropdownStyle, maxHeight: '250px', overflowY: 'auto' }} ref={listRef}>
                    <div className={styles.header}>
                        <div>ID</div>
                        <div>Descrição</div>
                        <div style={{textAlign: 'right'}}>Prazo Médio</div>
                    </div>
                    
                    {loading ? (
                        <div style={{padding: '10px', textAlign: 'center', color: '#666'}}>Carregando...</div>
                    ) : filteredConditions.length > 0 ? (
                        filteredConditions.map((condition, index) => (
                            <div 
                                key={condition.ID} 
                                className={styles.item}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSelect(condition);
                                }}
                                onClick={() => handleSelect(condition)}
                                style={{ backgroundColor: index === selectedIndex ? '#dbeafe' : undefined }}
                            >
                                <div className={styles.colId}>{condition.ID}</div>
                                <div className={styles.colDesc}>{condition.DESCRICAO}</div>
                                <div className={styles.colPrazo}>{condition.PRAZO_MEDIO} Dias</div>
                            </div>
                        ))
                    ) : (
                        <div style={{padding: '10px', textAlign: 'center', color: '#888'}}>
                            Nenhuma condição encontrada.
                        </div>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

export default PaymentConditionSelect;